import React from 'react';
import {TypographyHeading1} from './TypographyHeading1';
import {TypographyHeading2} from './TypographyHeading2';
import {TypographyHeading3} from './TypographyHeading3';
import {TypographyHeading4} from './TypographyHeading4';
import {TypographyHeading5} from './TypographyHeading5';
import {TypographyParagraph} from './TypographyParagraph';
import {TypographyButton} from './TypographyButton';
import {RichTextSpan} from '../text/RichTextSpan';
import styled from 'styled-components';
import {AccentColorOptions, CombinedTextColorOptions, useCurrentAccentColor} from 'epic-ue-theme';

export interface TextColorProps {
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
}
interface DefaultProps {
    variant?: undefined;
}
export interface Heading1Props {
    variant: 'h1';
    petit?: boolean;
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
}

interface HeadingProps {
    variant: 'h2' | 'h3' | 'h4';
    big?: boolean;
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
}

interface Heading5Props {
    variant: 'h5';
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
}

export interface ParagraphProps {
    variant: 'p';
    big?: boolean;
    caption?: boolean;
    tinyCaption?: boolean;
    giant?: boolean;
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
}

interface ButtonProps {
    variant: 'button';
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
    big?: boolean;
}

export type AsTagsForSEO = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span';

export interface TypographyChildrenProps {
    children: React.ReactNode;
    color?: CombinedTextColorOptions;
    accentColor?: AccentColorOptions;
    as?: AsTagsForSEO;
    className?: string;
}

export type TypographyProps =
    | Heading1Props
    | HeadingProps
    | Heading5Props
    | ParagraphProps
    | ButtonProps
    | DefaultProps;

export const Typography = ({
    variant = 'h3',
    children,
    color,
    accentColor,
    as,
    className,
    ...props
}: TypographyChildrenProps & TypographyProps): JSX.Element => {
    const {petit} = props as Heading1Props;
    const {big, caption, tinyCaption, giant} = props as ParagraphProps;
    const {name} = useCurrentAccentColor();
    const currentAccentColor = accentColor ? accentColor : (name as AccentColorOptions);

    const content =
        typeof children === 'string' ? (
            <RichTextSpan
                accentColor={currentAccentColor}
                className="typography-rich-text"
                richText={children}
            />
        ) : (
            children
        );

    switch (variant) {
        case 'h1':
            return (
                <TypographyHeading1 petit={petit} color={color} as={as} className={className}>
                    {content}
                </TypographyHeading1>
            );
        case 'h2':
            return (
                <TypographyHeading2 big={big} color={color} as={as} className={className}>
                    {content}
                </TypographyHeading2>
            );
        case 'h3':
            return (
                <TypographyHeading3 big={big} color={color} as={as} className={className}>
                    {content}
                </TypographyHeading3>
            );
        case 'h4':
            return (
                <TypographyHeading4 big={big} color={color} as={as} className={className}>
                    {content}
                </TypographyHeading4>
            );
        case 'h5':
            return (
                <TypographyHeading5 color={color} as={as} className={className}>
                    {content}
                </TypographyHeading5>
            );
        case 'p':
            return (
                <TypographyParagraph
                    big={big}
                    caption={caption}
                    tinyCaption={tinyCaption}
                    giant={giant}
                    color={color}
                    as={as}
                    className={className}>
                    {content}
                </TypographyParagraph>
            );
        case 'button':
            return (
                <TypographyButton color={color} big={big} as={as} className={className}>
                    {content}
                </TypographyButton>
            );
        default:
            return (
                <TypographyHeading3 big={big} color={color} as={as} className={className}>
                    {content}
                </TypographyHeading3>
            );
    }
};

export const TextColor = styled.div<TextColorProps>`
    color: ${(p) => (p.color ? p.theme.palette.text[p.color] : p.theme.palette.text.primary)};
`;
